import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { LogOut, Menu, X, Bell } from "lucide-react";

// IMPORTAÇÕES DO FIREBASE PARA AS NOTIFICAÇÕES
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../config/firebase";

// Importando a logo oficial (a mesma do Login) para usar no centro do Mobile
import logoOriginal from "../../assets/images/logo_frotabaase_transp.png";

const Header = ({ isSidebarOpen, onToggleSidebar }) => {
  const { userInfo, companyInfo, logout } = useAuth();
  const navigate = useNavigate();

  // Estado para o contador de notificações e controle de limpeza
  const [notificationCount, setNotificationCount] = useState(0);
  const [isCleaned, setIsCleaned] = useState(false);

  // ==========================================
  // LÓGICA DO SININHO (Tempo Real)
  // ==========================================
  useEffect(() => {
    if (!userInfo) return;

    let q;

    // 🏠 CENÁRIO A: Imobiliária (Ouve as pendências dela)
    if (companyInfo?.id) {
      q = query(
        collection(db, "requests"),
        where("companyId", "==", companyInfo.id),
        where("status", "==", "Pendente")
      );
    } 
    // 🛡️ CENÁRIO B: Elite Real Benefícios (Admin ou Equipe Interna)
    else if (userInfo?.role === "admin" || userInfo?.isInternal || userInfo?.email === 'kadoflorence@gmail.com') {
      q = query(
        collection(db, "requests"),
        where("status", "==", "Em Analise")
      );
    } else {
      return;
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Se o snapshot mudou (entrou algo novo), resetamos o status de "limpo"
      setIsCleaned(false);
      setNotificationCount(snapshot.size);
    }, (error) => {
      // 🛡️ SILENCIADOR: Evita spam de erro 403 no console se as permissões ainda estiverem propagando
      if (error.code === 'permission-denied') return;
      console.error("Erro ao buscar notificações em tempo real:", error);
    });

    return () => unsubscribe();
  }, [userInfo, companyInfo]);

  const handleLogout = async () => {
    try {
      if (logout) await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      window.location.href = "/login";
    }
  };

  // 🚀 FUNÇÃO PARA CLICAR NO SINO (LIMPA E REDIRECIONA PARA /REQUESTS)
  const handleBellClick = () => {
    setIsCleaned(true); // Zera o badge visualmente na hora
    
    if (companyInfo?.id) {
      navigate("/dashboard/requests");
    } else {
      navigate("/super-admin/requests");
    }
  };

  const isMainLayout = !!onToggleSidebar;

  // O contador final respeita se o usuário acabou de "limpar" visualmente
  const displayCount = isCleaned ? 0 : notificationCount;

  return (
    <header
      className={`fixed top-0 right-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200/60 dark:border-slate-700/60 transition-all duration-300 ease-in-out left-0 ${
        isMainLayout ? "lg:left-64" : ""
      }`}
    >
      {/* Container principal com relative para permitir centralização absoluta da logo no mobile */}
      <div className="flex items-center justify-between w-full h-[72px] px-4 sm:px-6 lg:px-8 relative">
        
        {/* 1. ESQUERDA: Botão de Menu Mobile */}
        <div className="flex items-center lg:hidden z-10">
          {onToggleSidebar && (
            <button
              className="p-2 -ml-2 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-xl transition-colors"
              onClick={onToggleSidebar}
              aria-label={isSidebarOpen ? "Fechar menu" : "Abrir menu"}
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>

        {/* O flex-1 vazio ajuda no alinhamento do desktop quando o menu hamburger some */}
        <div className="hidden lg:block flex-1 z-10"></div>

        {/* 2. CENTRO: Logo Oficial Centralizada (APENAS MOBILE) */}
        {onToggleSidebar && (
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 lg:hidden flex items-center justify-center pointer-events-none z-0">
            <img 
              src={logoOriginal} 
              alt="Real Benefícios" 
              className="h-7 sm:h-8 w-auto object-contain drop-shadow-sm" 
            />
          </div>
        )}

        {/* 3. DIREITA: Notificações, Info do Usuário e Logout */}
        <div className="flex flex-1 lg:flex-none justify-end items-center z-10">
          {userInfo && (
            <div className="flex items-center space-x-2 sm:space-x-5">
              
              {/* 🚀 O SININHO DE NOTIFICAÇÕES (COM BADGE DINÂMICO E NOVA ROTA) */}
              <button
                onClick={handleBellClick}
                className="relative p-2 rounded-xl text-slate-400 hover:text-[#00a99d] hover:bg-teal-50 dark:hover:bg-slate-800/50 transition-colors group"
                title="Notificações e Pendências"
              >
                <Bell size={20} className={displayCount > 0 ? "animate-swing" : ""} />
                
                {displayCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white ring-2 ring-white dark:ring-slate-900 shadow-sm animate-pulse">
                    {displayCount > 9 ? "9+" : displayCount}
                  </span>
                )}
              </button>

              <div className="h-8 w-px bg-slate-200/60 dark:bg-slate-700/60 hidden sm:block"></div>

              {/* INFORMAÇÕES DO USUÁRIO */}
              <div className="flex-col items-end hidden sm:flex">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 leading-tight">
                  {userInfo.name || userInfo.email?.split('@')[0]}
                </span>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">
                  {companyInfo?.nome || (userInfo.isInternal || userInfo.role === 'admin' ? 'Elite - Torre de Controle' : 'Imobiliária Parceira')}
                </span>
              </div>

              <div className="h-8 w-px bg-slate-200/60 dark:bg-slate-700/60 hidden sm:block"></div>
              
              {/* BOTÃO DE SAIR */}
              <button
                onClick={handleLogout}
                className="p-2 rounded-xl text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-colors"
                title="Sair do sistema"
              >
                <LogOut size={20} />
              </button>

            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;